import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { progressService } from '../../services/progressService';
import CourseDashboard from './CourseDashboard';
import { DATA_VIZ_CHALLENGES as CHALLENGES_INTRO } from './config/dataVizIntroChallenges';
import { DATA_VIZ_CHALLENGES as CHALLENGES_INTERMEDIATE } from './config/dataVizIntermediateChallenges';
import { DATA_VIZ_CHALLENGES as CHALLENGES_1 } from './config/dataVizChallenges';
import { DATA_VIZ_CHALLENGES as CHALLENGES_2 } from './config/dataViz2Challenges';
import { DATA_VIZ_CHALLENGES as CHALLENGES_3 } from './config/dataViz3Challenges';
import SEO from "../common/SEO";

export const DATA_VIZ_METADATA = [
    {
        id: 'data_viz_intro',
        title: 'Foundations: Core Chart Types',
        badge: 'Foundations',
        ctaLabel: 'Start Learning',
        cardDescription: 'Five charts. Five questions. Learn when Bar, Line, Pie, Stacked Bar, and Area are the right tool - using real video streaming data.',
        scenario: 'Charts are the language of data. This module covers the five chart types every analyst reaches for daily - not just how to build them, but when to use each one and why it communicates better than the alternatives.',
        introMission: 'Work through 5 guided questions using a real video streaming dataset. Each question focuses on one chart type and explains the thinking behind choosing it.',
        category: 'Data Viz Foundations',
        challengeCount: CHALLENGES_INTRO.length,
        challenges: CHALLENGES_INTRO,
        duration: '20 mins',
        route: '/exercises/data-viz-intro'
    },
    {
        id: 'data_viz_intermediate',
        title: 'Beyond the Basics: Advanced Charts',
        badge: 'Intermediate',
        ctaLabel: 'Start Learning',
        cardDescription: 'Histogram, Heatmap, Map, Box Plot, Scatter - five charts built for distribution, geography, and multi-variable analysis. Real e-commerce data.',
        scenario: 'Once you know the core five, these chart types unlock a new level of analysis: understanding distributions, surfacing seasonal hot spots, mapping geography, comparing statistical spreads, and exploring correlations between variables.',
        introMission: 'Five guided questions using a real e-commerce dataset. Each question demonstrates a specific analytical problem that a standard bar or line chart can\'t solve, and shows why a more specialised chart type is the right tool.',
        category: 'Data Viz Intermediate',
        challengeCount: CHALLENGES_INTERMEDIATE.length,
        challenges: CHALLENGES_INTERMEDIATE,
        duration: '25 mins',
        route: '/exercises/data-viz-intermediate'
    },
    {
        id: 'data_viz_1',
        title: 'Is Growth Slowing Down in the UK?',
        scenario: 'The Product Team believes that user growth in the UK has started to slow down, but they aren\'t sure why. They need your help to investigate the data and find out if it\'s an acquisition issue, an engagement drop, or a problem with a specific platform.',
        introMission: 'Answer the 10 questions step by step to identify the root cause of the UK growth slowdown. Use the hints if you get stuck!',
        category: 'Product Analytics',
        challengeCount: CHALLENGES_1.length,
        challenges: CHALLENGES_1,
        duration: '45 mins',
        route: '/exercises/data-viz-1'
    },
    {
        id: 'data_viz_2',
        title: 'Customer Financial Behavior',
        scenario: 'Analyze customer geographic distribution, income brackets, and transaction behaviors using a FinTech dataset to uncover patterns in user spending.',
        introMission: 'Answer the 10 questions to identify geographic hotspots, income correlations, and the highest value customer segments.',
        category: 'Product Analytics',
        challengeCount: CHALLENGES_2.length,
        challenges: CHALLENGES_2,
        duration: '45 mins',
        route: '/exercises/data-viz-2'
    },
    {
        id: 'data_viz_3',
        title: 'Ride-Sharing Platform Operations',
        scenario: 'The Operations team wants to identify which cities offer the best opportunity for expansion. Investigate demand patterns, revenue quality, and driver performance across six cities to make a data-driven recommendation.',
        introMission: 'Work through 10 questions across demand analysis, revenue analysis, and efficiency metrics to build a complete picture of each city and recommend where to expand.',
        category: 'Product Analytics',
        challengeCount: CHALLENGES_3.length,
        challenges: CHALLENGES_3,
        duration: '45 mins',
        route: '/exercises/data-viz-3'
    }
];

/**
 * Converts raw progress data (from DB or localStorage) into the state shape
 * expected by CourseDashboard. Filters out stale/renamed challenge IDs so only
 * challenges that exist in the current config are counted.
 */
function buildProgressState(rawProgress) {
    const newCompleted = new Set();
    const newChallenges = {};
    DATA_VIZ_METADATA.forEach(module => {
        // Raw progress key uses hyphens to match how progressService stores DB case_ids:
        // e.g. module.id='data_viz_intro' → progressKey='case_data-viz-intro'
        const progressKey = `case_${module.id.replace(/_/g, '-')}`;
        const allIds = rawProgress.completedChallenges?.[progressKey] || [];
        // Filter to only IDs that exist in the current module config
        // This silently ignores stale DB rows (e.g. from before a challenge rename)
        const validIds = new Set(module.challenges.map(c => c.id));
        const filteredIds = allIds.filter(id => validIds.has(id));
        // CourseDashboard looks up by `case_${caseItem.id}` where id uses underscores
        newChallenges[`case_${module.id}`] = filteredIds;
        if (filteredIds.length >= module.challengeCount) {
            newCompleted.add(module.id);
        }
    });
    return { completedCases: newCompleted, completedChallenges: newChallenges };
}

const DataVizIndex = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Initialise synchronously from localStorage so completions show instantly
    // on mount - no waiting for the DB round-trip.
    const [completedCases, setCompletedCases] = useState(() => {
        const cached = progressService.getCachedProgress('data-viz');
        return buildProgressState(cached).completedCases;
    });
    const [completedChallenges, setCompletedChallenges] = useState(() => {
        const cached = progressService.getCachedProgress('data-viz');
        return buildProgressState(cached).completedChallenges;
    });

    // Authoritative DB load - overwrites the cached state once the query resolves.
    useEffect(() => {
        if (!user) return;
        const loadAllProgress = async () => {
            try {
                const progress = await progressService.loadExerciseProgress('data-viz')
                    .catch(() => ({ completedCases: [], completedChallenges: {} }));
                const { completedCases: newCompleted, completedChallenges: newChallenges } =
                    buildProgressState(progress);
                setCompletedCases(newCompleted);
                setCompletedChallenges(newChallenges);
            } catch (e) { /* keep cached state */ }
        };
        loadAllProgress();
    }, [user]);

    const completionPercentage = Math.round((completedCases.size / DATA_VIZ_METADATA.length) * 100);

    const handleStartCase = (index) => {
        const module = DATA_VIZ_METADATA[index];
        const caseKey = `case_${module.id}`;
        const completedIds = completedChallenges[caseKey] || [];
        if (completedIds.length === 0) {
            // No progress yet - show the intro
            navigate(module.route);
        } else {
            // Continue from first incomplete question
            const firstIncompleteIdx = module.challenges.findIndex(c => !completedIds.includes(c.id));
            if (firstIncompleteIdx === -1) {
                // All done - go to last question
                navigate(`${module.route}?q=${module.challengeCount}`);
            } else {
                navigate(`${module.route}?q=${firstIncompleteIdx + 1}`);
            }
        }
    };

    const handleChallengeClick = (caseIndex, challengeIndex) => {
        // DataViz Tool uses ?q=1 index (1-based)
        navigate(`${DATA_VIZ_METADATA[caseIndex].route}?q=${challengeIndex + 1}`);
    };

    return (
        <>
            <SEO title="Data Visualization Curriculum | DataHelix" description="Interactive data visualization modules designed for data analysts." />
            <CourseDashboard
                cases={DATA_VIZ_METADATA}
                completedCases={completedCases}
                completedChallenges={completedChallenges}
                onStartCase={handleStartCase}
                onChallengeClick={handleChallengeClick}
                completionPercentage={completionPercentage}
                title="Visualizing Data"
                subtitle="Master data visualization through five hands-on modules. Learn when to use each chart type and why it communicates better than the alternatives."
                accentColor="orange"
            />
        </>
    );
};

export default DataVizIndex;
